import { MovimientoCartera } from '../services/carteraService';
import { getTipoMovimientoLabel } from './movimientosCarterasLabels';

export interface ArqueoCarteraGrupo {
  totalIngresos: number;
  totalEgresos: number;
  saldo: number;
  cantidadMovimientos: number;
}

export interface ArqueoCarteraPorMcp extends ArqueoCarteraGrupo {
  mcp: string;
}

export interface ArqueoCarteraPorTipoPago extends ArqueoCarteraGrupo {
  tipoPago: string;
  descripcion: string;
}

export interface ArqueoCarteraPorTipoFormulario extends ArqueoCarteraGrupo {
  nroTipoFormulario: string;
  nombreTipoFormulario: string;
}

export interface ArqueoCarteraPorUsuario extends ArqueoCarteraGrupo {
  usuario: string;
}

export interface ArqueoCarteraPorEntidad extends ArqueoCarteraGrupo {
  nombre: string;
}

export interface ArqueoCarteraPorFecha extends ArqueoCarteraGrupo {
  fecha: string;
}

export type ArqueoCarteraDrillDownTipo =
  | 'mcp'
  | 'tipoPago'
  | 'tipoFormulario'
  | 'usuario'
  | 'entidad'
  | 'fecha';

export interface ArqueoCarteraDrillDown {
  tipo: ArqueoCarteraDrillDownTipo;
  valor: string;
  etiqueta: string;
}

const DRILL_DOWN_TAB_LABELS: Record<ArqueoCarteraDrillDownTipo, string> = {
  mcp: 'Medio',
  tipoPago: 'Tipo de pago',
  tipoFormulario: 'Tipo de formulario',
  usuario: 'Usuario',
  entidad: 'Entidad',
  fecha: 'Fecha',
};

export interface ArqueoCarteraResumen {
  totalIngresos: number;
  totalEgresos: number;
  saldoNeto: number;
  cantidadMovimientos: number;
  porMcp: ArqueoCarteraPorMcp[];
  porTipoPago: ArqueoCarteraPorTipoPago[];
  porTipoFormulario: ArqueoCarteraPorTipoFormulario[];
  porUsuario: ArqueoCarteraPorUsuario[];
  porEntidad: ArqueoCarteraPorEntidad[];
  porFecha: ArqueoCarteraPorFecha[];
}

type GrupoMutable = ArqueoCarteraGrupo;

const movimientosOperativos = (movimientos: MovimientoCartera[]): MovimientoCartera[] =>
  movimientos.filter((m) => m.nroFormulario !== 0);

const actualizarGrupo = (grupo: GrupoMutable, debe: number, haber: number) => {
  grupo.totalIngresos += debe;
  grupo.totalEgresos += haber;
  grupo.saldo = grupo.totalIngresos - grupo.totalEgresos;
  grupo.cantidadMovimientos += 1;
};

const ordenarPorSaldoAbs = <T extends ArqueoCarteraGrupo>(items: T[]): T[] =>
  [...items].sort((a, b) => Math.abs(b.saldo) - Math.abs(a.saldo));

type TipoPagoCategoria =
  | 'EFECTIVO'
  | 'TRANSFERENCIA'
  | 'CHEQUE_TERC'
  | 'CHEQUE_PROP'
  | 'TARJETA'
  | 'OTROS';

const TIPO_PAGO_LABELS: Record<TipoPagoCategoria, string> = {
  EFECTIVO: 'Efectivo',
  TRANSFERENCIA: 'Transferencias',
  CHEQUE_TERC: 'Cheque Terc.',
  CHEQUE_PROP: 'Cheque Prop.',
  TARJETA: 'Tarjeta',
  OTROS: 'Otros',
};

const resolverCategoriaTipoPago = (tipoPago?: string | null): TipoPagoCategoria => {
  const cod = (tipoPago ?? '').trim().toUpperCase();
  if (cod.startsWith('TB')) return 'TRANSFERENCIA';
  const c0 = cod.charAt(0);
  switch (c0) {
    case 'E':
      return 'EFECTIVO';
    case 'C':
      return 'CHEQUE_TERC';
    case 'P':
      return 'CHEQUE_PROP';
    case 'T':
      return 'TARJETA';
    default:
      return 'OTROS';
  }
};

export const getTipoPagoArqueoLabel = (tipoPago: string, detalle?: string): string => {
  const det = detalle?.trim();
  if (det) return det;
  const cod = tipoPago?.trim();
  if (cod) {
    const cat = resolverCategoriaTipoPago(cod);
    return TIPO_PAGO_LABELS[cat];
  }
  return 'Sin tipo de pago';
};

export const calcularArqueoCartera = (movimientos: MovimientoCartera[]): ArqueoCarteraResumen => {
  const operativos = movimientosOperativos(movimientos);

  const porMcpMap = new Map<string, ArqueoCarteraPorMcp>();
  const porTipoPagoMap = new Map<string, ArqueoCarteraPorTipoPago>();
  const porTipoMap = new Map<string, ArqueoCarteraPorTipoFormulario>();
  const porUsuarioMap = new Map<string, ArqueoCarteraPorUsuario>();
  const porEntidadMap = new Map<string, ArqueoCarteraPorEntidad>();
  const porFechaMap = new Map<string, ArqueoCarteraPorFecha>();

  let totalIngresos = 0;
  let totalEgresos = 0;

  for (const mov of operativos) {
    const debe = Number(mov.debe) || 0;
    const haber = Number(mov.haber) || 0;
    totalIngresos += debe;
    totalEgresos += haber;

    const mcp = mov.mcp?.trim() || 'Sin MCP';
    if (!porMcpMap.has(mcp)) {
      porMcpMap.set(mcp, { mcp, totalIngresos: 0, totalEgresos: 0, saldo: 0, cantidadMovimientos: 0 });
    }
    actualizarGrupo(porMcpMap.get(mcp)!, debe, haber);

    const tipoPago = mov.tipoPago?.trim().toUpperCase() || '—';
    if (!porTipoPagoMap.has(tipoPago)) {
      porTipoPagoMap.set(tipoPago, {
        tipoPago,
        descripcion: getTipoPagoArqueoLabel(tipoPago),
        totalIngresos: 0,
        totalEgresos: 0,
        saldo: 0,
        cantidadMovimientos: 0,
      });
    }
    actualizarGrupo(porTipoPagoMap.get(tipoPago)!, debe, haber);

    const nroTipo = mov.nroTipoFormulario?.trim() || '—';
    if (!porTipoMap.has(nroTipo)) {
      porTipoMap.set(nroTipo, {
        nroTipoFormulario: nroTipo,
        nombreTipoFormulario: getTipoMovimientoLabel(mov),
        totalIngresos: 0,
        totalEgresos: 0,
        saldo: 0,
        cantidadMovimientos: 0,
      });
    }
    actualizarGrupo(porTipoMap.get(nroTipo)!, debe, haber);

    const usuario = mov.usuario?.trim() || 'Sin usuario';
    if (!porUsuarioMap.has(usuario)) {
      porUsuarioMap.set(usuario, {
        usuario,
        totalIngresos: 0,
        totalEgresos: 0,
        saldo: 0,
        cantidadMovimientos: 0,
      });
    }
    actualizarGrupo(porUsuarioMap.get(usuario)!, debe, haber);

    const entidad = mov.nombre?.trim() || 'Sin entidad';
    if (!porEntidadMap.has(entidad)) {
      porEntidadMap.set(entidad, {
        nombre: entidad,
        totalIngresos: 0,
        totalEgresos: 0,
        saldo: 0,
        cantidadMovimientos: 0,
      });
    }
    actualizarGrupo(porEntidadMap.get(entidad)!, debe, haber);

    const fecha = mov.fechaEmision?.slice(0, 10) || 'Sin fecha';
    if (!porFechaMap.has(fecha)) {
      porFechaMap.set(fecha, {
        fecha,
        totalIngresos: 0,
        totalEgresos: 0,
        saldo: 0,
        cantidadMovimientos: 0,
      });
    }
    actualizarGrupo(porFechaMap.get(fecha)!, debe, haber);
  }

  const porFecha = Array.from(porFechaMap.values()).sort((a, b) => b.fecha.localeCompare(a.fecha));

  return {
    totalIngresos,
    totalEgresos,
    saldoNeto: totalIngresos - totalEgresos,
    cantidadMovimientos: operativos.length,
    porMcp: ordenarPorSaldoAbs(Array.from(porMcpMap.values())),
    porTipoPago: ordenarPorSaldoAbs(Array.from(porTipoPagoMap.values())),
    porTipoFormulario: ordenarPorSaldoAbs(Array.from(porTipoMap.values())),
    porUsuario: ordenarPorSaldoAbs(Array.from(porUsuarioMap.values())),
    porEntidad: ordenarPorSaldoAbs(Array.from(porEntidadMap.values())),
    porFecha,
  };
};

export const formatEtiquetaDrillDownArqueo = (filtro: ArqueoCarteraDrillDown): string =>
  `${DRILL_DOWN_TAB_LABELS[filtro.tipo]}: ${filtro.etiqueta}`;

export const movimientoCoincideDrillDownArqueo = (
  mov: MovimientoCartera,
  filtro: ArqueoCarteraDrillDown
): boolean => {
  if (mov.nroFormulario === 0) return false;

  switch (filtro.tipo) {
    case 'mcp':
      return (mov.mcp?.trim() || 'Sin MCP') === filtro.valor;
    case 'tipoPago':
      return (mov.tipoPago?.trim().toUpperCase() || '—') === filtro.valor;
    case 'tipoFormulario':
      return (mov.nroTipoFormulario?.trim() || '—') === filtro.valor;
    case 'usuario':
      return (mov.usuario?.trim() || 'Sin usuario') === filtro.valor;
    case 'entidad':
      return (mov.nombre?.trim() || 'Sin entidad') === filtro.valor;
    case 'fecha':
      return (mov.fechaEmision?.slice(0, 10) || 'Sin fecha') === filtro.valor;
    default:
      return true;
  }
};

export const filtrarMovimientosDrillDownArqueo = (
  movimientos: MovimientoCartera[],
  filtro: ArqueoCarteraDrillDown | null
): MovimientoCartera[] => {
  if (!filtro) return movimientos;
  return movimientos.filter((mov) => movimientoCoincideDrillDownArqueo(mov, filtro));
};

export const accentColorArqueoCartera = (seed: string): string => {
  const palette = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#00838f', '#5d4037', '#455a64'];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
};
